import { Playground, PLAYGROUNG_BG_LIGHTER } from '../../../components/playground/Playground';
import { PlaygroundClasses } from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Headline } from '../../../components/Headline';
import { Note } from '../../../components/Note';

import Divider from '@rio-cloud/rio-uikit/components/divider/Divider';

const eachShadowVariants = ['subtle', 'muted', 'accent', 'default', 'smooth', 'hard', 'focused'];

const eachDownShadows = ['muted-down', 'default-down'];

const eachShadowDirections = ['smooth-up', 'smooth-down', 'hard-up', 'hard-down'];

const eachHoverShadows = ['none', ...eachShadowVariants, ...eachDownShadows, ...eachShadowDirections];

export const Shadows = () => (
    <div>
        <Headline anchor='shadows'>Shadows</Headline>
        <Description subline='Shadow class for panels and blocks'>
            <p>
                The default shadow class is intended to be used on panels or blocks which should stand out from the
                light gray background and should be avoided to put everywhere. Use it to emphasize certain blocks or
                panels e.g. on a dashboard or items of a drag and drop list.
            </p>
            <p>
                <b>Do not use hard shadows on tables, in dialogs, or on elements placed on white background.</b>
            </p>
            <p>
                The exception for not using shadows on a white background is for floating elements placed above the
                content for instance via position absolute.
            </p>
        </Description>
        <Playground example={shadowDefaultExample} classes={shadowClasses} background={PLAYGROUNG_BG_LIGHTER} />

        <Headline anchor='shadow-variants' headlineType='h3'>
            Shadow variants
        </Headline>
        <Playground example={shadowExample} classes={shadowClasses} background={PLAYGROUNG_BG_LIGHTER} />

        <Headline anchor='hover-shadows' headlineType='h3'>
            Hover shadows
        </Headline>
        <Description>
            <p>
                Use <code className='code-inline'>hover-shadow-*</code> class to choose the hover elevation. Transition
                is included by default. For the tight focused preset use{' '}
                <code className='code-inline'>shadow-focused</code>.
            </p>
        </Description>
        <Playground example={hoverShadowsExample} classes={shadowClasses} background={PLAYGROUNG_BG_LIGHTER} />
    </div>
);

const shadowExample = (
    <div>
        <Headline anchor='surrounding-shadows' headlineType='h4' headlineNoTopSpace>
            Surrounding shadows
        </Headline>
        <div className='display-grid grid-cols-4 gap-20 text-color-darker'>
            {eachShadowVariants.map(value => (
                <div
                    className={`height-100 rounded bg-white shadow-${value} display-grid place-items-center`}
                    key={crypto.randomUUID()}
                >
                    <span className={`hidden-xs ${value === 'default' ? 'text-medium' : ''}`}>shadow-{value}</span>
                </div>
            ))}
        </div>

        <Headline anchor='direction-shadows' headlineType='h4' className='margin-top-50'>
            Direction shadows
        </Headline>
        <div className='display-grid grid-cols-4 gap-20 text-color-darker'>
            {eachDownShadows.map(value => (
                <div
                    className={`
                        height-100 rounded bg-white shadow-${value} display-grid place-items-center
                    `}
                    key={crypto.randomUUID()}
                >
                    <span className='hidden-xs'>shadow-{value}</span>
                </div>
            ))}
        </div>

        <div className='display-grid grid-cols-4 gap-20 text-color-darker margin-top-50'>
            {eachShadowDirections.map(value => (
                <div
                    className={`
                        height-100 rounded bg-white shadow-${value} display-grid place-items-center
                    `}
                    key={crypto.randomUUID()}
                >
                    <span className='hidden-xs'>shadow-{value}</span>
                </div>
            ))}
        </div>

        <Divider className='margin-top-20' />
        <Headline anchor='accent-shadow-for-list-items' headlineType='h4'>
            Accent shadow for list items
        </Headline>
        <Description>
            <Note>
                When utilizing the <code className='code-inline'>shadow-accent</code> avoid combining it with a border
                style, as the shadow is optimized for small block items like those in a list and{' '}
                <b>works best without a border</b>.
            </Note>
        </Description>
        <div className='display-grid grid-cols-2 column-gap-25 row-gap-5 max-width-600'>
            {Array.from({ length: 6 }, value => (
                <div
                    className={`
                        height-80 rounded bg-white shadow-accent display-grid place-items-center
                    `}
                    key={crypto.randomUUID()}
                >
                    <span>Item</span>
                </div>
            ))}
        </div>
    </div>
);

const shadowDefaultExample = (
    <div className='display-grid grid-cols-2 gap-20 text-color-darker'>
        <div
            className='
            height-100 panel panel-default panel-body margin-bottom-0
            shadow-default display-grid place-items-center
        '
        >
            shadow-default
        </div>
        <div className='height-100 panel panel-default margin-bottom-0 shadow-default'>
            <div className='panel-heading'>Panel with shadow</div>
            <div className='panel-body display-grid place-items-center'>shadow-default</div>
        </div>
    </div>
);

const shadowClasses = (
    <>
        <PlaygroundClasses label='Variants' prefix='shadow-' classes={eachShadowVariants} />
        <PlaygroundClasses
            label='Direction shadows'
            prefix='shadow-'
            classes={[...eachDownShadows, ...eachShadowDirections]}
        />
        <PlaygroundClasses label='Hover shadows' prefix='hover-shadow-' classes={eachHoverShadows} />
        <PlaygroundClasses label='Focused shadow' classes={['shadow-focused']} />
    </>
);

const hoverShadowsExample = (
    <div>
        <div className='display-grid grid-cols-3 gap-20 text-color-darker margin-bottom-25'>
            <div className='shadow-none hover-shadow-accent display-grid place-items-center height-100 rounded bg-white '>
                <span className='hidden-xs'>hover-shadow-accent</span>
            </div>
            <div className='shadow-none border hover-shadow-default display-grid place-items-center height-100 rounded bg-white '>
                <span className='hidden-xs'>hover-shadow-default</span>
            </div>
            <div className='shadow-default hover-shadow-hard-down border display-grid place-items-center height-100 rounded bg-white '>
                <span className='hidden-xs'>hover-shadow-hard-down</span>
            </div>
            <div className='shadow-default hover-shadow-focused display-grid place-items-center height-100 rounded bg-white '>
                <span className='hidden-xs'>hover-shadow-focused</span>
            </div>
        </div>

        <h5>Custom hover settings</h5>
        <p>
            Customize hover animation with CSS vars:
            <code className='code-inline'>--shadow-hover-duration</code>,
            <code className='code-inline'>--shadow-hover-easing</code>, and
            <code className='code-inline'>--shadow-hover-delay</code>
            <br />
            Defaults are{' '}
            <code className='code-inline'>
                var(--shadow-hover-duration, 140ms) var(--shadow-hover-easing, ease-out) var(--shadow-hover-delay, 0ms)
            </code>
        </p>
        <div className='display-grid grid-cols-3 gap-20'>
            <div
                className='shadow-default hover-shadow-focused display-grid place-items-center height-100 rounded bg-white max-width-500'
                style={
                    {
                        '--shadow-hover-duration': '50ms',
                    } as React.CSSProperties
                }
            >
                <span className='hidden-xs'>hover-shadow-focused (custom)</span>
            </div>
            <div
                className='shadow-default hover-shadow-focused display-grid place-items-center height-100 rounded bg-white max-width-500'
                style={
                    {
                        '--shadow-hover-duration': '250ms',
                        '--shadow-hover-delay': '50ms',
                    } as React.CSSProperties
                }
            >
                <span className='hidden-xs'>hover-shadow-focused (custom)</span>
            </div>
        </div>
    </div>
);
